import { useState, type FormEvent } from 'react';
import { api, errorText } from '../../api';
import { fmtDate } from '../../lib/format';
import { useToast } from '../../lib/toast';
import { betragText, ibanText } from './logik';
import type { AmpelSicht, BankkontoSicht, DateiSicht, MonatAntwort, UnlesbarSicht } from './types';

const konten = '/api/kanzlei/buchen/bankkonten';

function Ampel({ a, name }: { a: AmpelSicht; name: string }) {
  return (
    <li className={a.gruen ? 'ok' : 'bad'}>
      <span className="sr-only">
        {name}: {a.gruen ? 'grün' : 'rot'}.{' '}
      </span>
      {a.satz}
    </li>
  );
}

// Je aktivem Bankkonto die zwei Ampeln mit Satz und die Salden (Anfang, Eingänge, Ausgänge, Ende)
export function KontoAmpeln({ k }: { k: BankkontoSicht }) {
  const s = k.saldo;
  // Beginnt der Anfang erst nach dem Monatsersten, steht der Tag dabei
  const anfangSpaet = s.anfangTag && !s.anfangTag.endsWith('-01') ? s.anfangTag : null;
  return (
    <div className="card bk">
      <div className="bk-h">
        <b>
          {k.bezeichnung} <span className="num">{k.finanzkonto}</span>
        </b>
        <span className="num muted">{ibanText(k.iban)}</span>
      </div>
      <ul className="amp">
        <Ampel a={k.ampelAuszug} name="Auszug" />
        <Ampel a={k.ampelGebucht} name="Gebucht" />
      </ul>
      <dl className="bk-saldo">
        <div>
          <dt>Anfang{anfangSpaet ? ` ${fmtDate(anfangSpaet)}` : ''}</dt>
          <dd className="num">{s.anfangCents == null ? 'fehlt' : betragText(s.anfangCents, { euro: true })}</dd>
        </div>
        <div>
          <dt>Eingänge</dt>
          <dd className="num pos">{betragText(s.eingaengeCents, { plus: true, euro: true })}</dd>
        </div>
        <div>
          <dt>Ausgänge</dt>
          <dd className="num neg">{betragText(-Math.abs(s.ausgaengeCents), { euro: true })}</dd>
        </div>
        <div>
          <dt>Ende</dt>
          <dd className="num">{s.endeCents == null ? 'fehlt' : betragText(s.endeCents, { euro: true })}</dd>
        </div>
        {s.differenzCents != null && s.differenzCents !== 0 && (
          <div className="bad">
            <dt>Differenz</dt>
            <dd className="num">{betragText(s.differenzCents, { plus: true, euro: true })}</dd>
          </div>
        )}
      </dl>
      {k.saetze.length > 0 && (
        <ul className="bk-diag">
          {k.saetze.map((x, i) => (
            <li key={i}>{x}</li>
          ))}
        </ul>
      )}
    </div>
  );
}

// Karte „Neues Bankkonto“: nur solange die Nummer nicht bestätigt ist. Nummer wie im Steuerprogramm.
export function BankkontoKarte({ k, onFertig }: { k: BankkontoSicht; onFertig(): Promise<void> }) {
  const toast = useToast();
  const [fk, setFk] = useState(k.finanzkonto);
  const [bez, setBez] = useState(k.bezeichnung);
  const [busy, setBusy] = useState(false);
  const [fehler, setFehler] = useState<string | null>(null);

  const senden = async (body: { finanzkonto?: string; bezeichnung?: string; status: 'aktiv' | 'nicht_buchen' }) => {
    setBusy(true);
    setFehler(null);
    try {
      await api.post(`${konten}/${k.id}`, body);
      await onFertig();
      toast(body.status === 'aktiv' ? `Bankkonto ${body.finanzkonto ?? k.finanzkonto} bestätigt` : 'Bankkonto wird nicht gebucht');
    } catch (err) {
      setFehler(errorText(err));
    } finally {
      setBusy(false);
    }
  };
  const bestaetigen = (e: FormEvent) => {
    e.preventDefault();
    const nr = fk.trim();
    if (!/^\d{4}$/.test(nr)) {
      setFehler('Bitte eine vierstellige Kontonummer eintragen, z. B. 1200.');
      return;
    }
    void senden({ finanzkonto: nr, bezeichnung: bez.trim() || k.bezeichnung, status: 'aktiv' });
  };
  return (
    <form className="card bk-neu" onSubmit={bestaetigen} aria-label={`Neues Bankkonto ${ibanText(k.iban)}`}>
      <div className="ch">
        <h3>Neues Bankkonto {ibanText(k.iban)}</h3>
        <span>Nummer wie im Steuerprogramm</span>
      </div>
      <div className="bk-neu-r">
        <label>
          Finanzkonto
          <input className="num" value={fk} inputMode="numeric" maxLength={4} required onChange={(e) => setFk(e.target.value.replace(/\D/g, ''))} />
        </label>
        <label>
          Bezeichnung
          <input value={bez} maxLength={40} onChange={(e) => setBez(e.target.value)} />
        </label>
        <span className="bk-actions">
          <button type="submit" className="btn sm dark" disabled={busy}>
            Bestätigen
          </button>
          <button type="button" className="btn sm" disabled={busy} onClick={() => void senden({ status: 'nicht_buchen' })}>
            Nicht buchen
          </button>
        </span>
      </div>
      {fehler && (
        <p className="zd-err" role="alert">
          {fehler}
        </p>
      )}
    </form>
  );
}

// Karte „Anfangssaldo übernehmen?“: Der Auszug beginnt erst nach dem Monatsersten (etwa im ersten Monat eines Kontos).
// Ein Klick bestätigt genau diesen Tag und Betrag; fehlt der Auszug davor, gehört er hochgeladen.
export function AnfangKarte({ k, url, onFertig }: { k: BankkontoSicht; url: string; onFertig(d: MonatAntwort): void }) {
  const toast = useToast();
  const [busy, setBusy] = useState(false);
  const [fehler, setFehler] = useState<string | null>(null);
  const a = k.anfangBestaetigen;
  if (!a) return null;
  const uebernehmen = async () => {
    setBusy(true);
    setFehler(null);
    try {
      onFertig(await api.post<MonatAntwort>(url, { bankkontoId: k.id, tag: a.tag, anfangCents: a.anfangCents }));
      toast('Anfangssaldo übernommen');
    } catch (err) {
      setFehler(errorText(err));
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="card bk-neu" role="group" aria-label={`Anfangssaldo ${k.bezeichnung} ${k.finanzkonto}`}>
      <div className="ch">
        <h3>
          Anfangssaldo {k.bezeichnung} <span className="num">{k.finanzkonto}</span>
        </h3>
        <span>Fehlt ein Auszug davor, bitte erst diesen hochladen.</span>
      </div>
      <div className="bk-neu-r">
        <span>
          Der Auszug beginnt erst am {fmtDate(a.tag)}: Anfangssaldo{' '}
          <span className="num">{betragText(a.anfangCents, { euro: true })}</span> übernehmen?
        </span>
        <span className="bk-actions">
          <button type="button" className="btn sm dark" disabled={busy} onClick={() => void uebernehmen()}>
            Übernehmen
          </button>
        </span>
      </div>
      {fehler && (
        <p className="zd-err" role="alert">
          {fehler}
        </p>
      )}
    </div>
  );
}

// Bankkonto steht auf „nicht buchen“: eine Zeile mit „Doch buchen“
export function NichtBuchen({ k, onFertig }: { k: BankkontoSicht; onFertig(): Promise<void> }) {
  const toast = useToast();
  const [busy, setBusy] = useState(false);
  const an = async () => {
    setBusy(true);
    try {
      await api.post(`${konten}/${k.id}`, { status: 'aktiv' });
      await onFertig();
    } catch (err) {
      toast(errorText(err));
    } finally {
      setBusy(false);
    }
  };
  return (
    <p className="bk-aus note">
      {k.bezeichnung} {ibanText(k.iban)} wird nicht gebucht.{' '}
      <button type="button" className="linkbtn" disabled={busy} onClick={() => void an()}>
        Doch buchen
      </button>
    </p>
  );
}

const DATEI_STATUS: Record<DateiSicht['status'], { cls: string; text: string }> = {
  gelesen: { cls: 'p-ok', text: 'gelesen' },
  fehler: { cls: 'p-bad', text: 'Fehler' },
  nur_beleg: { cls: 'p-neu', text: 'nur Beleg' },
  neu: { cls: 'p-info', text: 'neu' },
};

// Satz zu einer Datei, nur im Sonderfall: Fehler, nur als Beleg abgelegt (etwa ein Scan: CAMT oder CSV anfordern),
// gelesen mit Hinweis (nur Kontostände, kein Bankkonto erkannt, fremde Währung) oder keinem Bankkonto zugeordnet
function dateiHinweis(f: DateiSicht): { satz: string; rot: boolean } | null {
  if (f.status === 'fehler') return { satz: f.satz ?? 'Die Datei ließ sich nicht lesen.', rot: true };
  if (f.status === 'nur_beleg') return f.satz ? { satz: f.satz, rot: false } : null;
  if (f.status !== 'gelesen') return null;
  if (f.satz) return { satz: f.satz, rot: false };
  return f.bankkontoId ? null : { satz: 'Gehört zu keinem Bankkonto und zählt nicht für die Ampel.', rot: false };
}

// Hinweise nur im Sonderfall: je Datei der Satz, was mit ihr ist und was zu tun ist
export function DateiHinweise({
  dateien,
  onBeleg,
  onNachtragen,
}: {
  dateien: DateiSicht[];
  onBeleg(id: string): void;
  onNachtragen?: (f: DateiSicht, u: UnlesbarSicht) => void;
}) {
  const liste = dateien.flatMap((f) => {
    const h = dateiHinweis(f);
    return h ? [{ f, ...h }] : [];
  });
  const unlesbar = dateien.filter((f) => f.unlesbar?.length);
  if (!liste.length && !unlesbar.length) return null;
  return (
    <div className="bu-hinw">
      {liste.map(({ f, satz, rot }) => (
        <div key={f.belegId} className={rot ? 'lock lk-bad' : 'lock'} role="status">
          <span>
            <button type="button" className="linkbtn" onClick={() => onBeleg(f.belegId)}>
              {f.name}
            </button>
            : {satz}
          </span>
        </div>
      ))}
      {/* Sam 2.11.2026: markieren, welche Zeilen die KI im Scan nicht lesen und buchen konnte */}
      {unlesbar.map((f) => (
        <div key={`u-${f.belegId}`} className="lock bu-unl" role="status">
          <div>
            <p>
              <button type="button" className="linkbtn" onClick={() => onBeleg(f.belegId)}>
                {f.name}
              </button>
              :{' '}
              {f.unlesbar.length === 1
                ? 'Diese Zeile konnte die KI nicht sicher lesen, sie ist nicht gebucht.'
                : `Diese ${f.unlesbar.length} Zeilen konnte die KI nicht sicher lesen, sie sind nicht gebucht.`}{' '}
              Bitte im Auszug nachsehen und von Hand nachtragen.
            </p>
            <ul>
              {f.unlesbar.map((u, i) => (
                <li key={i}>
                  <span>{u.satz}</span>
                  {onNachtragen && (
                    <button type="button" className="linkbtn" onClick={() => onNachtragen(f, u)}>
                      Nachtragen
                    </button>
                  )}
                </li>
              ))}
            </ul>
          </div>
        </div>
      ))}
    </div>
  );
}

// Format der Datei in Worten (die Codes bleiben intern)
const FORMAT: Record<string, string> = {
  camt053: 'CAMT',
  mt940: 'MT940',
  csv: 'CSV',
  pdf_text: 'PDF',
  pdf_scan: 'PDF (Scan)',
  zip: 'ZIP',
};

// Dateienliste (klein, eingeklappt): alle Auszugsdateien des Monats
export function DateienListe({ dateien, onBeleg }: { dateien: DateiSicht[]; onBeleg(id: string): void }) {
  if (!dateien.length) return null;
  return (
    <details className="bu-dat">
      <summary>
        {dateien.length === 1 ? '1 Auszugsdatei' : `${dateien.length} Auszugsdateien`}
      </summary>
      <ul>
        {dateien.map((f) => {
          const st = DATEI_STATUS[f.status];
          const teile = [
            f.format ? (FORMAT[f.format] ?? null) : null,
            f.von && f.bis ? `${fmtDate(f.von)} bis ${fmtDate(f.bis)}` : null,
            f.status === 'gelesen' ? (f.anzahl === 1 ? '1 Zeile' : `${f.anzahl} Zeilen`) : null,
          ].filter(Boolean);
          return (
            <li key={f.belegId}>
              <button type="button" className="linkbtn" onClick={() => onBeleg(f.belegId)}>
                {f.name}
              </button>{' '}
              <span className={`pill ${st.cls}`}>{st.text}</span>
              {teile.length > 0 && <span className="note"> {teile.join(' · ')}</span>}
            </li>
          );
        })}
      </ul>
    </details>
  );
}
